import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, User, Clock, ChevronRight, Home, HelpCircle, List, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";
import Disclaimer from "@/components/Disclaimer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fetchGuide, fetchDevice } from "@/lib/api";
import { CATEGORIES } from "@/data/static";

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, j) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={j} className="font-semibold text-white">{p.slice(2, -2)}</strong>
      : <span key={j}>{p}</span>
  );
}

function renderInner(text, keyPrefix) {
  const lines = text.split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return null;
  if (lines.every((l) => l.trim().startsWith("- "))) {
    return (
      <ul key={`${keyPrefix}-ul`} className="mt-3 space-y-2 pl-1">
        {lines.map((l, j) => (
          <li key={j} className="flex gap-2 text-sm leading-relaxed text-slate-300 sm:text-base">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon" />
            <span>{renderInline(l.trim().replace(/^-\s+/, ""))}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p key={`${keyPrefix}-p`} className="mt-3 text-sm leading-relaxed text-slate-300 sm:text-base">{renderInline(lines.join(" "))}</p>;
}

function renderBody(body) {
  return body.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      const nl = block.indexOf("\n");
      const heading = (nl === -1 ? block : block.slice(0, nl)).replace("## ", "").trim();
      const rest = nl === -1 ? "" : block.slice(nl + 1);
      const id = heading.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return (
        <div key={i}>
          <h2 id={id} className="mt-8 scroll-mt-24 font-heading text-xl font-bold text-white sm:text-2xl">{heading}</h2>
          {rest.trim() && renderInner(rest, `h2-${i}`)}
        </div>
      );
    }
    if (block.startsWith("### ")) {
      const nl = block.indexOf("\n");
      const heading = (nl === -1 ? block : block.slice(0, nl)).replace("### ", "").trim();
      const rest = nl === -1 ? "" : block.slice(nl + 1);
      return (
        <div key={i}>
          <h3 className="mt-6 scroll-mt-24 font-heading text-base font-bold text-neon sm:text-lg">{heading}</h3>
          {rest.trim() && renderInner(rest, `h3-${i}`)}
        </div>
      );
    }
    return <div key={i}>{renderInner(block, `b-${i}`)}</div>;
  });
}

export default function GuideDetail() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [relDevices, setRelDevices] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setGuide(null); setNotFound(false); setRelDevices([]);
    fetchGuide(slug)
      .then((g) => {
        setGuide(g);
        Promise.all((g.related_devices || []).map((s) => fetchDevice(s).then((r) => r.device).catch(() => null))).then((arr) => setRelDevices(arr.filter(Boolean)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="flex min-h-[50vh] items-center justify-center text-slate-400">Guide not found. <Link to="/guides" className="ml-1 text-cyber underline">Back to guides</Link></div>;
  if (!guide) return <div className="flex min-h-[50vh] items-center justify-center text-slate-400">Loading…</div>;

  const scrollToHeading = (label) => {
    const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 88;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: guide.title,
      image: guide.cover_image,
      author: { "@type": "Organization", name: guide.author },
      datePublished: guide.published_date,
      dateModified: guide.updated_date,
      description: guide.excerpt,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (guide.faqs || []).map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <>
      <Seo title={guide.title} description={guide.excerpt} path={`/guides/${slug}`} jsonLd={jsonLd} />

      <article className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <nav className="mb-5 flex items-center gap-1.5 text-xs text-slate-500">
          <Link to="/" className="hover:text-white"><Home className="h-3.5 w-3.5" /></Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/guides" className="hover:text-white">Guides</Link>
          <ChevronRight className="h-3 w-3" /><span className="line-clamp-1 text-slate-300">{guide.title}</span>
        </nav>

        <div className="flex flex-wrap gap-1.5">
          {(guide.tags || []).map((t) => <span key={t} className="rounded-md border border-border bg-graphite px-2 py-0.5 text-[10px] font-medium text-slate-400">{t}</span>)}
        </div>
        <h1 className="mt-3 font-display text-2xl font-black leading-tight tracking-tight text-white sm:text-4xl">{guide.title}</h1>

        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {guide.author}</span>
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Published {guide.published_date}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Updated {guide.updated_date} · {guide.read_minutes} min</span>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-slateSurface">
          <img src={guide.cover_image} alt={guide.title} className="aspect-[16/9] w-full object-cover" />
        </div>

        {guide.risk_disclaimer && (
          <div className="mt-6"><Disclaimer testid="guide-risk-disclaimer">
            <strong className="text-slate-100">Advisory:</strong> This article covers market-sensitive topics (used-kit condition, CPID, patched status or taxes). It is informational only, not legal advice or an endorsement. Rules and device policies change - verify current status with official/relevant sources.
          </Disclaimer></div>
        )}

        {(slug.includes("pta") || slug.includes("dirbs") || slug.includes("imei")) && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyber/30 bg-cyber/5 p-5" data-testid="guide-pta-tool-card">
            <p className="text-sm text-slate-300">Need the exact tax number for your phone?</p>
            <a
              href="https://phoneflip.pk/pta-calculator"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-1.5 rounded-lg bg-cyber px-4 py-2 text-sm font-heading font-bold text-charcoal hover:shadow-[0_0_18px_rgba(0,163,255,0.5)]"
            >
              Calculate PTA Tax on PhoneFlip <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}

        {/* TOC */}
        {guide.toc?.length > 0 && (
          <div className="mt-6 rounded-xl border border-border bg-slateSurface p-5">
            <div className="mb-2 flex items-center gap-2 text-sm font-heading font-bold text-white"><List className="h-4 w-4 text-neon" /> Table of Contents</div>
            <ol className="space-y-1.5">
              {guide.toc.map((t) => (
                <li key={t}><button type="button" data-testid={`toc-link-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => scrollToHeading(t)} className="text-left text-sm text-slate-400 hover:text-neon">→ {t}</button></li>
              ))}
            </ol>
          </div>
        )}

        <div className="mt-2">{renderBody(guide.body)}</div>

        {/* FAQ */}
        {guide.faqs?.length > 0 && (
          <section className="mt-10">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-bold text-white"><HelpCircle className="h-5 w-5 text-cyber" /> FAQs</h2>
            <Accordion type="single" collapsible className="rounded-xl border border-border bg-slateSurface px-4">
              {guide.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f-${i}`} className="border-border">
                  <AccordionTrigger className="text-left text-sm font-medium text-white hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-300">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Author bio */}
        {guide.author_bio && (
          <section className="mt-10 flex gap-4 rounded-xl border border-border bg-slateSurface p-5" data-testid="guide-author-bio">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-graphite text-neon">
              <User className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-heading font-bold text-white">{guide.author}</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-400 sm:text-sm">{guide.author_bio}</p>
            </div>
          </section>
        )}

        {/* internal links */}
        <section className="mt-10 grid gap-6 sm:grid-cols-2">
          {relDevices.length > 0 && (
            <div>
              <h3 className="mb-3 font-heading text-sm font-bold text-white">Devices mentioned</h3>
              <ul className="space-y-2">
                {relDevices.map((d) => <li key={d.slug}><Link to={`/device/${d.slug}`} className="text-sm text-slate-300 hover:text-neon">→ {d.name}</Link></li>)}
              </ul>
            </div>
          )}
          {guide.related_categories?.length > 0 && (
            <div>
              <h3 className="mb-3 font-heading text-sm font-bold text-white">Related categories</h3>
              <ul className="space-y-2">
                {guide.related_categories.map((c) => CATEGORIES[c] && <li key={c}><Link to={`/category/${c}`} className="text-sm text-slate-300 hover:text-cyber">→ {CATEGORIES[c].title}</Link></li>)}
              </ul>
            </div>
          )}
        </section>
      </article>
    </>
  );
}
